import { useState, useEffect, useMemo, useCallback, useRef } from "react";

import * as Material from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import FilterListIcon from "@mui/icons-material/FilterList";
import AppShortcutIcon from "@mui/icons-material/AppShortcut";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import AddIcon from "@mui/icons-material/Add";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import HubIcon from "@mui/icons-material/Hub";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import MainButton from "../../SmallComponents/MainButton";

import CustomModal from "../../CustomModal";

import { createTheme, ThemeProvider } from "@mui/material/styles";

import api from "../../../helpers/api.js";

import Config, { token } from "../../../config.js";

import LinearProgress from "@mui/material/LinearProgress";

import Screen from "../../Screen";
import Pagination from "../../Pagination";

import { CreateModal } from "../../../pages/Groups";

import "./styles.css";

import { useChallenge } from "../../../contexts/ChallengeContext";
import { useAuth } from "../../../contexts/AuthContext";

import { makeStyles } from "@mui/styles";

import {
  Elements,
  useStripe,
  useElements,
  CardElement,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

export default function PaymentCard({ open, setOpen }) {
  const { currentChallenge, groups } = useChallenge();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState();

  const stripe = useStripe();
  const elements = useElements();
  const [plan, setPlan] = useState("YEARLY");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: user.username,
          email: user.email,
        },
      });

      const { data } = await api.post("/billing/subscribe", {
        challenge_id: currentChallenge.challenge_id,
        plan: Config.payments.plans[plan],
        payment_method: paymentMethod,
        recurrency: plan,
      });

      if (data?.paymentIntent) {
        if (data.paymentIntent?.status === "paid") {
          setTimeout(() => {
            window.location.reload()
          }, 2500);
          setSuccess(true);
          setError(null);
        }
      } else {
        if (data?.error) {
          const { error } = data;
          if (error.decline_code === "insufficient_funds") {
            setError("Cartão com créditos insuficientes");
          } else if (error.code === "expired_card") {
            setError("Este cartão não pode ser usado, pois está expirado");
          } else if (error.code === "incorrect_cvc") {
            setError(
              "Código de segurança incorreto, verifique-o e tente novamente"
            );
          } else if (error.code === "incorrect_number") {
            setError(
              "O número inserido parece não estar correto, verifique o número do cartão e tente novamente"
            );
          }
        }

        setError(
          "Houve um erro no processamento da sua assinatura, verifique os dados do cartão inserido, e tente novamente"
        );
      }
    } catch (error) {
      console.error(error);
      setError(
        "Houve um erro inesperado no processamento da sua assinatura, verifique os dados do cartão inserido, e tente novamente"
      );
    }

    setLoading(false);
  };

  function CardField({ label, component, xs = 12 }) {
    return (
      <Material.Grid item xs={xs}>
        <Material.TextField
          size="small"
          variant="outlined"
          fullWidth
          label={label}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputComponent: component,
            inputProps: {
              options: CARD_ELEMENT_OPTIONS,
              autoFocus: true,
            },
          }}
        />
      </Material.Grid>
    );
  }

  function PlanChoice({
    title,
    value,
    subtitle,
    priceBig,
    priceSmall,
    priceOffer,
    priceSubtitle,
    topOffer,
  }) {
    return (
      <div
        className={"offer-card mt-1 " + (plan === value && "checked")}
        onClick={() => setPlan(value)}
      >
        <div>
          <Material.Radio checked={plan === value} />
        </div>
        <div className="title-box">
          <h4>{title}</h4>
          <small>{subtitle}</small>
        </div>
        <div className="price-box">
          <label className="price-offer">{priceOffer}</label>

          <h4>
            <b>{priceBig}</b>
            <small>{priceSmall}</small>
          </h4>
          <small className="price-subtitle">{priceSubtitle}</small>
        </div>
        {topOffer && <span className="top-offer">{topOffer}</span>}
      </div>
    );
  }

  const MEMO_plans = useMemo(() => {
    return (
      <>
        <PlanChoice
          title="Plano anual"
          value="YEARLY"
          subtitle="Economia de R$480,00"
          priceBig="R$564"
          priceSmall=",00 / ano"
          priceOffer={
            <>
              de <span style={{ textDecoration: "line-through" }}>R$1044,00</span>{" "}
              por
            </>
          }
          priceSubtitle="R$47,00/mês - média"
          topOffer="RECOMENDADO  |  46% OFF"
        />
        <PlanChoice
          title="Plano mensal"
          value="MONTHLY"
          priceBig="R$87"
          priceSmall=",00"
          priceSubtitle="R$1044,00 no ano"
        />
      </>
    );
  }, [plan]);

  const MEMO_card = useMemo(() => {
    return (
      <Material.Grid container spacing={2} className="mt-2 mb-3">
        <CardField label="Número do Cartão" component={CardNumberElement} />
        <CardField label="Validade" component={CardExpiryElement} xs={8} />
        <CardField label="CVC" component={CardCvcElement} xs={4} />
      </Material.Grid>
    );
  }, []);

  return (
    <>
      <CustomModal
        maxWidth={400}
        bottomButtonCancelText="Cancelar"
        hideSecondaryButton={true}
        loading={loading || success}
        title="Iniciar assinatura"
        handleSubmit={handleSubmit}
        bottomButtonSumbitText={
          "Assinar por " + (plan === "YEARLY" ? "R$ 564,00 / ano" : "R$ 87,00 / mês")
        }
        open={open}
        handleClose={() => setOpen(false)}
        width="100%"
        bodyWrapperStyle={{}}
        modalClass="select-challenge-modal"
        bottomButtonsOrientation="vertical"
        bodyComponent={
          <div className="container">
            <h4 className="mb-3">
              Escolha um dos planos abaixo, e ative sua assinatura
            </h4>
            <form onSubmit={handleSubmit}>
              {MEMO_plans}

              {MEMO_card}

              <small style={{ opacity: 0.65 }}>
                *a assinatura será cobrada automaticamente
              </small>

              {success && (
                <Material.Alert
                  key={Math.random()}
                  className="mt-1 slide-bottom-fade"
                  severity="success"
                >
                  Pagamento realizado com sucesso
                </Material.Alert>
              )}

              {error && (
                <Material.Alert
                  key={Math.random()}
                  className="mt-1 slide-bottom-fade"
                  severity="error"
                >
                  {error}
                </Material.Alert>
              )}
            </form>
          </div>
        }
      />
    </>
  );
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
    },
  },
};
